import { all, put, race, select, take, takeEvery, delay, fork, cancel } from 'redux-saga/effects'
import { FAILED, SUCCESS } from 'crom-api-constants'
import FileSaver from 'file-saver'
import _ from 'lodash'

import { getLocale, getToken } from '@global/selectors'

import { error, success } from '../../../../../../../../services/toastr'

import { sendMessage } from '../../../../../../../../services/socket/helpers'
import { getAppState } from '../../../../../../../../services/appState/selectors'

import {
    entityStackAddNotification,
    changeEntityStackTab,
    openEntityStack,
    swChangeEntityStackTab,
} from '../../../../actions'

import {
    EXPORTS_CREATE_PDF_TASK_REQUEST,
    EXPORTS_CREATE_PDF_TASK,
    EXPORTS_CREATE_XLS_TASK_REQUEST,
    EXPORTS_CREATE_XLS_TASK,
    EXPORTS_GET_TASKS_STATUS_REQUEST,
    EXPORTS_GET_TASK_FILE_REQUEST,
    EXPORTS_GET_TASK_FILE,
    EXPORTS_GET_TASK_STATUS_REQUEST,
    EXPORTS_GET_TASK_STATUS,
    EXPORTS_INIT_LOAD,
    EXPORTS_JOB_ADD_WATCHER,
    EXPORTS_JOB_REMOVE_WATCHER,
    EXPORTS_TASK_CHANGE,
    EXPORTS_TASK_REMOVE,
    LOCALE_PREFIX,
    TASK_STATES,
    WS_ENTITY_STACK_ADD_NOTIFICATION,
    WS_ENTITY_STACK_REMOVE_NOTIFICATION,
    WS_EXPORTS_TASK_RECEIVED,
    WS_EXPORTS_SET_LOADING_TRUE,
    WS_EXPORTS_SET_LOADING_FALSE,
    WS_EXPORTS_SET_LOADED_TRUE,
} from './constants'

import {
    createPdfExportTaskRequest,
    createXlsExportTaskRequest,
    exportsAddWatcher,
    exportsGetTasksStatusRequest,
    exportsGetTaskFileRequest,
    exportsGetTaskStatus,
    exportsGetTaskStatusRequest,
    exportsRemoveWatcher,
    exportsSetLoadedTrue,
    exportsSetLoadingFalse,
    exportsSetLoadingTrue,
    exportsTaskChange,
    exportsTaskReceived,
    exportsUpdateAppState,
} from './actions'

import { getJobData } from './selectors'

const EXPORTS_CREATE_PDF_TASK_REQUEST_SUCCESS = (module, id) =>
    `${EXPORTS_CREATE_PDF_TASK_REQUEST(module, id)}_${SUCCESS}`
const EXPORTS_CREATE_PDF_TASK_REQUEST_FAILED = (module, id) =>
    `${EXPORTS_CREATE_PDF_TASK_REQUEST(module, id)}_${FAILED}`

const EXPORTS_CREATE_XLS_TASK_REQUEST_SUCCESS = (module, id) =>
    `${EXPORTS_CREATE_XLS_TASK_REQUEST(module, id)}_${SUCCESS}`
const EXPORTS_CREATE_XLS_TASK_REQUEST_FAILED = (module, id) =>
    `${EXPORTS_CREATE_XLS_TASK_REQUEST(module, id)}_${FAILED}`

const EXPORTS_GET_TASK_STATUS_REQUEST_SUCCESS = (jobId) => `${EXPORTS_GET_TASK_STATUS_REQUEST(jobId)}_${SUCCESS}`
const EXPORTS_GET_TASK_STATUS_REQUEST_FAILED = (jobId) => `${EXPORTS_GET_TASK_STATUS_REQUEST(jobId)}_${FAILED}`

const EXPORTS_GET_TASK_FILE_REQUEST_SUCCESS = (jobId) => `${EXPORTS_GET_TASK_FILE_REQUEST(jobId)}_${SUCCESS}`
const EXPORTS_GET_TASK_FILE_REQUEST_FAILED = (jobId) => `${EXPORTS_GET_TASK_FILE_REQUEST(jobId)}_${FAILED}`

const EXPORTS_GET_TASKS_STATUS_REQUEST_SUCCESS = `${EXPORTS_GET_TASKS_STATUS_REQUEST}_${SUCCESS}`
const EXPORTS_GET_TASKS_STATUS_REQUEST_FAILED = `${EXPORTS_GET_TASKS_STATUS_REQUEST}_${FAILED}`

function* exportsInitLoad() {
    const appState = yield select(getAppState)
    const jobs = appState?.exports?.jobs ?? {}
    const jobIds = Object.keys(jobs)

    if (_.isEmpty(jobIds)) return null

    yield put(exportsSetLoadingTrue())
    sendMessage(WS_EXPORTS_SET_LOADING_TRUE)

    yield put(exportsGetTasksStatusRequest(jobIds))
    const result = yield race({
        success: take(EXPORTS_GET_TASKS_STATUS_REQUEST_SUCCESS),
        failed: take(EXPORTS_GET_TASKS_STATUS_REQUEST_FAILED),
    })

    if (result.failed) {
        console.error('Exports get tasks status request failed') // eslint-disable-line

        yield put(exportsSetLoadingFalse())
        yield put(exportsSetLoadedTrue())

        return null
    }

    const activeJobs = _.reduce(
        result.success?.payload?.response?.body,
        (acc, val) => {
            if (val?.state === TASK_STATES.SUCCEEDED)
                return {
                    ...acc,
                    [val?.id]: {
                        ...(jobs?.[val?.id] ?? {}),
                    },
                }

            return acc
        },
        {}
    )

    yield all(_.map(activeJobs, (job) => put(exportsTaskChange(job?.id, job))))

    yield put(exportsSetLoadingFalse())
    sendMessage(WS_EXPORTS_SET_LOADING_FALSE)

    yield put(exportsSetLoadedTrue())
    sendMessage(WS_EXPORTS_SET_LOADED_TRUE)

    return null
}

function* createPdfBackgroundTaskCallSaga(action) {
    const module = action?.data?.module
    const id = action?.data?.id
    if (!module || !id) return null

    const settings = action?.data?.settings
    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const result = yield all({
        response: race({
            success: take(EXPORTS_CREATE_PDF_TASK_REQUEST_SUCCESS(module, id)),
            failed: take(EXPORTS_CREATE_PDF_TASK_REQUEST_FAILED(module, id)),
        }),
        request: put(createPdfExportTaskRequest(module, id, settings, locale, token)),
    })

    if (result.response.failed) {
        error({
            id: `${LOCALE_PREFIX}.errorDuringCreation`,
            values: { id },
        })
        return null
    }

    const jobId = result.response.success?.payload?.response?.body?.jobId
    const meta = result.response.success?.payload?.response?.body?.meta

    yield put(exportsTaskChange(jobId, { module, id: jobId, entId: id, meta, settings, state: 'ENQUEUED' }))

    const fileStatusResult = yield all({
        response: race({
            success: take(EXPORTS_GET_TASK_STATUS_REQUEST_SUCCESS(jobId)),
            failed: take(EXPORTS_GET_TASK_STATUS_REQUEST_FAILED(jobId)),
        }),
        request: put(exportsGetTaskStatusRequest(jobId)),
    })

    if (fileStatusResult.response.failed) {
        error({
            id: `${LOCALE_PREFIX}.errorGettingState`,
            values: { jobId },
        })
        return null
    }

    const jobBody = fileStatusResult.response.success?.payload?.response?.body

    yield put(exportsTaskChange(jobId, { id: jobId, entId: id, module, meta, settings, ...jobBody }))
    success({
        id: `${LOCALE_PREFIX}.successfulTaskCreation`,
    })

    // Open entity stack and set active tab to exports
    yield put(changeEntityStackTab('exports'))
    yield put(swChangeEntityStackTab('exports'))

    sendMessage(WS_ENTITY_STACK_REMOVE_NOTIFICATION, { tabType: 'exports' })

    yield put(openEntityStack())

    // Add task status watcher
    yield put(exportsAddWatcher(module, id, jobId))

    return null
}

function* createXlsBackgroundTaskCallSaga(action) {
    const module = action?.data?.module
    const id = action?.data?.id
    if (!module || !id) return null

    const settings = action?.data?.settings
    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const result = yield all({
        response: race({
            success: take(EXPORTS_CREATE_XLS_TASK_REQUEST_SUCCESS(module, id)),
            failed: take(EXPORTS_CREATE_XLS_TASK_REQUEST_FAILED(module, id)),
        }),
        request: put(createXlsExportTaskRequest(module, id, settings, locale, token)),
    })

    if (result.response.failed) {
        error({
            id: `${LOCALE_PREFIX}.errorDuringCreation`,
            values: { id },
        })
        return null
    }

    const jobId = result.response.success?.payload?.response?.body?.jobId
    const meta = result.response.success?.payload?.response?.body?.meta

    yield put(exportsTaskChange(jobId, { module, id: jobId, entId: id, meta, settings, state: 'ENQUEUED' }))

    const fileStatusResult = yield all({
        response: race({
            success: take(EXPORTS_GET_TASK_STATUS_REQUEST_SUCCESS(jobId)),
            failed: take(EXPORTS_GET_TASK_STATUS_REQUEST_FAILED(jobId)),
        }),
        request: put(exportsGetTaskStatusRequest(jobId)),
    })

    if (fileStatusResult.response.failed) {
        error({
            id: `${LOCALE_PREFIX}.errorGettingState`,
            values: { jobId },
        })
        return null
    }

    const jobBody = fileStatusResult.response.success?.payload?.response?.body

    yield put(exportsTaskChange(jobId, { module, id: jobId, entId: id, meta, settings, ...jobBody }))
    success({
        id: `${LOCALE_PREFIX}.successfulTaskCreation`,
    })

    // Open entity stack and set active tab to exports
    yield put(changeEntityStackTab('exports'))
    yield put(swChangeEntityStackTab('exports'))

    sendMessage(WS_ENTITY_STACK_REMOVE_NOTIFICATION, { tabType: 'exports' })

    yield put(openEntityStack())

    yield put(exportsAddWatcher(module, id, jobId))

    return null
}

// Task status saga
function* exportsGetTaskStatusCallSaga(action) {
    const jobId = action?.data?.jobId
    const module = action?.data?.module
    const id = action?.data?.id

    if (!jobId || !module || !id) return null

    const result = yield all({
        response: race({
            success: take(EXPORTS_GET_TASK_STATUS_REQUEST_SUCCESS(jobId)),
            failed: take(EXPORTS_GET_TASK_STATUS_REQUEST_FAILED(jobId)),
        }),
        request: put(exportsGetTaskStatusRequest(jobId)),
    })

    const incomingJobBody = result.response.success?.payload?.response?.body

    // Failed state or failed request
    if (result.response.failed || incomingJobBody?.state === TASK_STATES.FAILED) {
        yield put(exportsTaskChange(jobId, { state: TASK_STATES.FAILED }))
        yield put(exportsRemoveWatcher(module, id, jobId))
        error({
            id: `${LOCALE_PREFIX}.errorDuringExport`,
            values: { jobId },
        })
        return null
    }

    // Finished export (succeeded state)
    if (incomingJobBody?.state === TASK_STATES.SUCCEEDED) {
        yield put(exportsTaskChange(jobId, incomingJobBody))
        yield put(exportsRemoveWatcher(module, id, jobId))
        yield put(exportsUpdateAppState())
        return null
    }

    // Other states (processing, enqueued, etc. )
    yield put(exportsTaskChange(jobId, incomingJobBody))

    return null
}

// File request saga
function* exportsGetTaskFileCallSaga(action) {
    const jobId = action?.data?.jobId
    const module = action?.data?.module
    const id = action?.data?.id

    if (!jobId || !module || !id) return null

    const locale = yield select(getLocale)

    yield put(exportsTaskChange(jobId, { state: TASK_STATES.DOWNLOADING }))

    const result = yield all({
        response: race({
            success: take(EXPORTS_GET_TASK_FILE_REQUEST_SUCCESS(jobId)),
            failed: take(EXPORTS_GET_TASK_FILE_REQUEST_FAILED(jobId)),
        }),
        request: put(exportsGetTaskFileRequest(jobId, locale)),
    })

    if (result.response.failed) {
        error({
            id: `${LOCALE_PREFIX}.errorGettingFile`,
            values: { jobId },
        })

        yield put(exportsTaskChange(jobId, { state: TASK_STATES.SUCCEEDED }))

        return null
    }

    const blob = result.response.success?.payload?.response?.body
    const jobData = yield select(getJobData, jobId)
    const fileName = jobData?.meta?.fileName

    yield put(exportsTaskChange(jobId, { state: TASK_STATES.SUCCEEDED }))

    FileSaver.saveAs(blob, fileName)
    success({
        id: `${LOCALE_PREFIX}.successfulFileSave`,
        values: { fileName },
    })

    return null
}

// Checks job status every 5 seconds
function* jobWatcher(action) {
    const module = action?.data?.module
    const id = action?.data?.entId
    const jobId = action?.data?.jobId

    if (!module || !id || !jobId) return null

    while (true) {
        yield put(exportsGetTaskStatus(module, id, jobId))
        yield delay(5000)
    }
}

// Job watcher control saga (starting and cancelling)
function* exportsJobWatcherCallSaga(action) {
    const module = action?.data?.module
    const id = action?.data?.entId
    const jobId = action?.data?.jobId

    if (!module || !id || !jobId) return null

    // Runs jobWatcher function
    const watcher = yield fork(jobWatcher, action)

    // If either the task is no longer active or has been removed - cancel the watcher
    yield take([EXPORTS_JOB_REMOVE_WATCHER(jobId), EXPORTS_TASK_REMOVE(jobId)])
    yield cancel(watcher)

    return null
}

// Saves changes made to a task
function* exportsTaskChangeCallSaga(action) {
    const jobId = action?.data?.jobId

    if (!jobId) return null

    const incomingChange = action?.data?.data
    const currentJobData = yield select(getJobData, jobId)

    const changeData = { ...currentJobData, ...incomingChange }

    if (currentJobData?.state !== incomingChange?.state) {
        sendMessage(WS_ENTITY_STACK_ADD_NOTIFICATION, { tabType: 'exports' })
        yield put(entityStackAddNotification('exports'))
    }

    sendMessage(WS_EXPORTS_TASK_RECEIVED, { jobId, changeData })
    yield put(exportsTaskReceived(jobId, changeData))

    return null
}

export default function* exportsSaga() {
    yield all([
        takeEvery(EXPORTS_INIT_LOAD, exportsInitLoad),
        takeEvery(EXPORTS_CREATE_PDF_TASK, createPdfBackgroundTaskCallSaga),
        takeEvery(EXPORTS_CREATE_XLS_TASK, createXlsBackgroundTaskCallSaga),
        takeEvery(EXPORTS_GET_TASK_STATUS, exportsGetTaskStatusCallSaga),
        takeEvery(EXPORTS_GET_TASK_FILE, exportsGetTaskFileCallSaga),
        takeEvery(EXPORTS_JOB_ADD_WATCHER, exportsJobWatcherCallSaga),
        takeEvery(EXPORTS_TASK_CHANGE, exportsTaskChangeCallSaga),
    ])
}
